import { EMPTY_GUID } from '../../../constants';
import { OcAuthResponse, OcChatConfig } from '../../../types/oc';
import { Messages, MessageStatus } from '../../Message/types/messages';
import {  ApiMessagesResponse,
  ChatHistoryItem,
  CrawlerJob,
  DiscussionPost,
  DiscussionPostDraft,
  DraftDiscussionRequest,
  OcCommunity,
  SubmitDiscussionRequest,
  SubmitDiscussionResponse } from '../types/apiTypes';
import { transformApiMessagesResponse } from './apiTransformers';
import { createOcApiClient, get, post } from './ocApiClient';

/**
 * OC Endpoints - Business logic for OC API operations
 * Uses the generic HTTP client to make specific API calls
 */

/**
 * Check if user is authenticated
 * Uses the Contacts WhoAmI endpoint to verify authentication
 * Note: This endpoint returns the Contact object directly, not wrapped in OcApiResponse
 */
export const checkOcAuth = async (
  config: OcChatConfig,
): Promise<OcAuthResponse> => {
  const client = createOcApiClient(config);

  try {
    const url = `${client.baseUrl}/contacts/getwhoami`;

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: client.headers,
    });

    // Handle authentication errors (401/403) - not logged in
    if (response.status === 401 || response.status === 403) {
      return { isAuthenticated: false };
    }

    // Handle server/network errors (500, etc.) - throw to show error state
    if (!response.ok) {
      throw new Error(`Authentication check failed: ${response.status} ${response.statusText}`);
    }

    // Parse response - this endpoint returns Contact object directly, not wrapped
    const data = await response.json();

    // If we got a ContactKey back, user is authenticated
    if (data && data.ContactKey && data.ContactKey !== EMPTY_GUID) {
      return {
        isAuthenticated: true,
        userId: data.ContactKey,
      };
    }

    return { isAuthenticated: false };
  } catch (error) {
    // Re-throw errors to show error state in UI (network issues, server errors, etc.)
    throw error instanceof Error ? error : new Error(String(error));
  }
};

/**
 * Initialize a new chat session
 * Calls CreateSession endpoint with aiAssistantKey
 */
export const initChatSession = async (
  config: OcChatConfig,
): Promise<Messages> => {
  const client = createOcApiClient(config);

  const data = await post<ApiMessagesResponse>(
    client,
    `/aiassistant/CreateSession?aiAssistantKey=${config.aiAssistantKey}`,
  );

  return transformApiMessagesResponse(data);
};

/**
 * Resume an existing chat session
 * Calls ResumeSession endpoint with aiAssistantKey and sessionKey
 */
export const resumeChatSession = async (
  config: OcChatConfig,
  sessionId: string,
): Promise<Messages> => {
  const client = createOcApiClient(config);

  const data = await post<ApiMessagesResponse>(
    client,
    `/aiassistant/ResumeSession?aiAssistantKey=${config.aiAssistantKey}&sessionKey=${sessionId}`,
  );

  return transformApiMessagesResponse(data);
};

/**
 * Send a message in a chat session
 * Calls SendMessage endpoint with request body containing AIAssistantKey, SessionKey, and Message
 */
export const sendChatMessage = async (
  config: OcChatConfig,
  message: string,
  sessionId: string,
): Promise<Messages> => {
  if (!message || !sessionId) {
    throw new Error('Message and sessionId are required');
  }

  const client = createOcApiClient(config);

  const data = await post<ApiMessagesResponse>(
    client,
    '/aiassistant/SendMessage',
    {
      AIAssistantKey: config.aiAssistantKey,
      SessionKey: sessionId,
      Message: message,
    },
  );

  return transformApiMessagesResponse(data);
};

/**
 * Send a message reaction (like/dislike)
 * Calls MessageReaction endpoint with request body containing SessionKey, MessageKey, and LikeStatus
 */
export const sendMessageReaction = async (
  config: OcChatConfig,
  messageId: string,
  status: MessageStatus,
  sessionId: string,
): Promise<boolean> => {
  if (!messageId || !sessionId) {
    throw new Error('MessageId and sessionId are required');
  }

  const client = createOcApiClient(config);

  try {
    await post(
      client, 
      '/aiassistant/MessageReaction',
      {
        SessionKey: sessionId,
        MessageKey: messageId,
        LikeStatus: status,
      }
    );
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Track message copy event
 * Calls MessageCopied endpoint with request body containing SessionKey and MessageKey
 */
export const trackMessageCopy = async (
  config: OcChatConfig,
  messageId: string,
  sessionId: string,
): Promise<boolean> => {
  if (!messageId || !sessionId) {
    throw new Error('MessageId and sessionId are required');
  }

  const client = createOcApiClient(config);

  try {
    await post(
      client, 
      '/aiassistant/MessageCopied',
      {
        SessionKey: sessionId,
        MessageKey: messageId,
      }
    );
    return true;
  } catch (error) {
    return false;
  }
};


/**
 * Get communities where user can contribute discussions
 * Calls GetCommunitiesCanContribute endpoint to fetch communities where the user can post discussions/messages
 */
export const getCommunitiesCanContribute = async (
  config: OcChatConfig,
): Promise<OcCommunity[]> => {
  const client = createOcApiClient(config);

  const data = await get<OcCommunity[]>(
    client,
    '/communities/GetCommunitiesCanContribute',
  );

  return data || [];
};

/**
 * Draft a discussion post using AI
 * Calls Draft endpoint with recipe code and session ID to get AI-generated content
 */
export const draftDiscussionPost = async (
  config: OcChatConfig,
  request: DraftDiscussionRequest,
): Promise<DiscussionPostDraft> => {
  const client = createOcApiClient(config);

  const data = await post<DiscussionPostDraft>(
    client,
    '/aiAssistant/Draft',
    request,
  );

  return data;
};

/**
 * Submit a discussion post to a community
 * Calls Enterprise API PostDiscussion endpoint with discussion details
 * Returns the full created discussion post object including URL
 */
export const submitDiscussionPost = async (
  config: OcChatConfig,
  request: SubmitDiscussionRequest,
): Promise<SubmitDiscussionResponse> => {
  const client = createOcApiClient(config);

  try {
    const response = await post<DiscussionPost>(
      client,
      '/discussions/PostDiscussionSimple',
      {
        CommunityKey: request.communityKey,
        Subject: request.subject,
        Message: request.body,
        TopicTagKeys: request.topicTagKeys || [],
      },
    );

    // API returns the full DiscussionPost object with LinkToMessage as the URL
    return {
      success: true,
      discussionPost: response,
      discussionUrl: response.LinkToMessage,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

export const fetchAiSearchResultData = async (
  config: OcChatConfig,
  searchPrompt: string): Promise<Messages> => {
  const client = createOcApiClient(config);

  const data = await post<ApiMessagesResponse>(
    client,
    '/aiassistant/Search',
    {
      searchPrompt: searchPrompt,
      aiAssistantKey: config.aiAssistantKey,
    },
  );

  return transformApiMessagesResponse(data);
};

/**
 * Get chat history for the user
 * Calls GetChatHistory endpoint to fetch user's chat sessions
 */
export const getChatHistory = async (
  config: OcChatConfig,
  originToolId: string,
): Promise<ChatHistoryItem[]> => {
  const client = createOcApiClient(config);

  const data = await get<ChatHistoryItem[]>(
    client,
    `/aiassistant/GetChatHistory?originToolId=${originToolId}`,
  );

  return data || [];
};

/**
 * Get crawler job information for the tenant for external source display
 * Calls GetCrawlerJobs endpoint to fetch tenant's crawler jobs
 */
export const getCrawlerJobs = async (
  config: OcChatConfig,
): Promise<CrawlerJob[]> => {
  const client = createOcApiClient(config);

  const data = await get<CrawlerJob[]>(
    client,
    `/aiassistant/GetCrawlerJobs`,
  );

  return data || [];
};

/**
 * Send user feedback on an AI message
 * Calls SendFeedback endpoint with request body containing SessionKey, MessageKey, and Feedback
 */
export const sendFeedback = async (
  config: OcChatConfig,
  sessionId: string,
  messageId: string,
  feedback: string,
): Promise<boolean> => {
  if (!sessionId || !messageId || !feedback) {
    throw new Error('SessionId, messageId, and feedback are required');
  }

  const client = createOcApiClient(config);

  try {
    await post(
      client,
      '/aiassistant/SendFeedback',
      {
        SessionKey: sessionId,
        MessageKey: messageId,
        Feedback: feedback.trim(),
      }
    );
    return true;
  } catch (error) {
    return false;
  }
};

